'use client'

import { useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import Image from 'next/image'
import Container from '@/components/structure/Container'
import Title from '@/components/content/common/Title'
import AppIcon from '@/components/content/app/AppIcon'
import Link from '@/components/ui/Link'
import { AppRank } from '@/components/content/app/AppRanking'
import RichText from '@/components/content/common/RichText'
import Pill from '@/components/ui/Pill'
import Annotation from '@/components/ui/Annotation'
import ScrollView from '@/components/ui/ScrollView'
import { Icon } from '@/icons/Icon'
import { Block } from '@/types/app'
import { FeaturedPlacement, trackFeaturedClick } from '@/lib/analytics'
import { easeMedium } from '@/lib/transitions'
import { Recommendation } from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import { createLogger } from '@/lib/logger'
import type { ReviewsApp } from '@/app/(app)/fetchers'
import type { Review, AppCategoryRecommendation, Category, Deal } from '@/payload-types'
import { determineDealsToDisplay } from '@/components/content/deals/helpers'
import { isPopulatedObject } from '@/lib/utils'

type HomeReviewsProps = {
  apps: ReviewsApp[]
}

type AppDockProps = {
  selectedApp: ReviewsApp | null
  setSelectedApp: (app: ReviewsApp) => void
}

const logger = createLogger('HomeReviews')

export default function HomeReviews({ apps }: HomeReviewsProps) {
  const [selectedApp, setSelectedApp] = useState<ReviewsApp | null>(apps?.[0] ?? null)

  if (!apps || apps.length === 0) {
    return null
  }

  return (
    <Container
      reduced={true}
      className="dark relative flex flex-col gap-16 rounded-3xl bg-black pb-20 md:pb-24"
    >
      <div className="bg-gradient relative z-10 rounded-3xl pt-10 md:pt-40">
        <Title
          title="We used them all.<br />This is what actually works."
          subtitle="App reviews"
          icon={<Icon name="review" />}
          paragraphs={[
            "You won't find reviews like this anywhere else — expert-written, no fluff.",
            'Know in <span>30</span> seconds if a tool is right for you.',
          ]}
          buttons={[
            { label: 'Explore top reviews', link: '/apps' },
            { label: 'Subscribe to updates', link: '/subscribe' },
          ]}
          type="primary"
          className="relative z-30"
        />

        <Image
          src="/img/home/stars-glyph.svg"
          alt="App reviews"
          width={720}
          height={370}
          className="user-select-none drag-none absolute left-1/2 top-0 z-20 -translate-x-1/2"
          style={{ height: 'auto' }}
        />
      </div>

      <AppDock apps={apps} selectedApp={selectedApp} setSelectedApp={setSelectedApp} />
      <VideoWalkthrough />

      <AnimatePresence mode="wait">
        {selectedApp && <AppReview key={selectedApp.id} app={selectedApp} />}
      </AnimatePresence>
      <Stars />
    </Container>
  )
}

function AppDock({ apps, selectedApp, setSelectedApp }: HomeReviewsProps & AppDockProps) {
  return (
    <div className="sticky top-20 z-40 bg-black/30 shadow-lg backdrop-blur-md">
      <div className="relative mx-auto w-full bg-[radial-gradient(ellipse_100.00%_40%_at_50.00%_-0.00%,_rgba(255,_255,_255,_0.12)_0%,_rgba(255,_255,_255,_0)_85%)] before:absolute before:left-0 before:right-0 before:top-0 before:h-px before:w-full before:bg-gradient-to-r before:from-transparent before:via-white/10 before:to-transparent">
        <ScrollView className="flex gap-6 px-6 py-5 md:justify-center">
          {apps.slice(0, 10).map((app) => {
            const isSelected = app.id === selectedApp?.id
            return (
              <div
                key={app.id}
                onClick={() => setSelectedApp(app)}
                className="relative w-fit cursor-pointer"
              >
                <AppIcon
                  icon={app.icon}
                  glyph={app.glyph}
                  name={app.name}
                  background={app.brandBackgroundColor}
                  size={48}
                  invert={true}
                  active={isSelected}
                />

                {isSelected && (
                  <motion.div
                    layoutId="selected-app"
                    transition={easeMedium}
                    className="absolute -bottom-3 left-1/2 z-20 h-1 w-1 -translate-x-1/2 rounded-full bg-primary-500"
                  ></motion.div>
                )}
              </div>
            )
          })}
        </ScrollView>
      </div>
    </div>
  )
}

function VideoWalkthrough() {
  return (
    <div className="relative w-full">
      {/*<div className="absolute left-0 top-0 z-20 h-px w-full bg-white/10"></div>
       <div className="relative aspect-video w-full">
        <Image
          src="/img/home/review-still.jpg"
          alt="App reviews"
          fill
          className="z-10 w-full object-cover"
        />
      </div> */}

      <div className="flex flex-col items-center">
        <span className="font-heading text-18 font-bold text-white">
          How we structure our reviews
        </span>
        <p className="text-white/60">Transparent, hands-on, and built for clarity</p>
      </div>
    </div>
  )
}

function AppReview({ app }: { app: ReviewsApp }) {
  // const review = app.review?.docs?.[0] as Review
  const [review] = (app.review?.docs || []) as Review[]
  if (!review) {
    logger.error('No review found for app', app)
    return null
  }
  const appCategoryRecommendations =
    (app.categoryRecommendations?.docs as AppCategoryRecommendation[]) || []
  const primaryAppCategoryRecommendation = appCategoryRecommendations.find(
    (rec) => rec.classification === 'primary',
  )
  const primaryCategory = primaryAppCategoryRecommendation?.category as Category
  const description =
    primaryAppCategoryRecommendation?.bestFor ?? review?.bestFor ?? app.description
  const recommendation = primaryAppCategoryRecommendation?.recommendation as Recommendation

  if (!primaryCategory?.id) {
    return null
  }

  const sectionApp = () => {
    return (
      <div className="relative">
        <Annotation
          label="App snapshot"
          position="left-5 translate-y-[31px] -translate-x-full z-30"
          align="left"
        />
        <Annotation
          label="Quick actions"
          position="-right-2 z-30 translate-y-[31px] translate-x-full"
          align="right"
        />

        <AppRank
          app={app}
          recommendation={recommendation}
          description={description}
          index={0}
          theme="dark"
          rank="none"
          className="rounded-2xl"
          flag={['demo']}
        />
      </div>
    )
  }

  const sectionTitle = (
    icon: string,
    title: string,
    value: string,
    type: string,
    annotate?: string,
  ) => {
    return (
      <div className="relative flex h-14 items-center justify-between gap-5 px-4.5">
        <div className="flex items-center gap-4">
          <Icon name={icon} size={18} className="text-primary-500" />
          <p className="text-16 font-semibold text-white">{title}</p>
        </div>

        <p className="flex gap-1 text-12 text-zinc-500">
          <span className="font-medium text-zinc-300">{value}</span>
          {type}
        </p>

        {annotate && (
          <Annotation label={annotate} position="right-0.5 translate-x-full" align="right" />
        )}
      </div>
    )
  }

  const sectionContent = () => {
    if (!review?.summary?.root?.children) {
      return null
    }

    return (
      <div className="relative flex flex-col p-5">
        <Annotation
          label="Summary section"
          position="left-3.5 -translate-x-full translate-y-[5px]"
          align="left"
        />

        <div className="relative text-white after:absolute after:bottom-0 after:left-0 after:h-full after:w-full after:bg-gradient-to-t after:from-black after:to-transparent">
          <RichText
            content={review.summary.root.children as Block[]}
            truncate={true}
            maxLength={400}
          />
        </div>
        <Link
          href={`/apps/${app.slug}`}
          onClick={() => trackFeaturedClick(FeaturedPlacement.HOME_REVIEW_CARDS, app.name)}
        >
          <Pill theme="dark" style="outlinePrimary">
            Read more
          </Pill>
        </Link>
      </div>
    )
  }

  return (
    <motion.div
      key={app.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={easeMedium}
      className="flex flex-col gap-12 px-3"
    >
      <div className="mx-auto flex w-full max-w-[584px] flex-col divide-y divide-white/15 rounded-2xl border border-white/15">
        {sectionApp()}

        {sectionTitle('circle-info', 'Summary', '1.2k', 'views')}
        {sectionContent()}

        {sectionTitle('wand-sparkle', 'Features', '7', 'features', 'Deep dive sections')}
        {sectionTitle(
          'clipboard-check',
          'Comparisons',
          String(app.comparisonsApps?.docs?.length ?? 0),
          'comparisons',
        )}

        {sectionTitle('xmark', 'Limitations', '2', 'limitations')}

        {sectionTitle('money-bill-coin', 'Pricing', '4', 'pricing tiers')}
        {sectionTitle(
          'gift',
          'Deals',
          String(
            determineDealsToDisplay(
              (app.deals?.docs ?? []).filter(isPopulatedObject<Deal>),
            ).length,
          ),
          'deals',
        )}
      </div>

      <div className="mx-auto flex h-fit w-fit flex-col items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 sm:flex-row sm:gap-4 sm:py-1.5">
        <div className="text-success-500">
          <Icon name="hexagon-check" />
        </div>
        <div className="flex flex-col items-center text-14 sm:flex-row sm:gap-1">
          <p className="text-white/60">
            Efficient App shares <span className="text-white">how every tool is tested.</span>
          </p>
          <Link href="/about" theme="primary" className="underlined primary">
            Read more
          </Link>
        </div>
      </div>
    </motion.div>
  )
}

function Stars() {
  return (
    <svg
      // `meticulous-ignore`: continuous `animate-spin` (15s rotation).
      // Different runs catch different rotational frames.
      data-review="ignore"
      className="meticulous-ignore mx-auto animate-spin"
      style={
        {
          '--duration': '15s',
        } as React.CSSProperties & { '--duration': string }
      }
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M20 0L21.0655 3.27343H24.4929L21.7073 5.28883L22.7728 8.5751L19.9872 6.54686L17.2015 8.5751L18.267 5.28883L15.4814 3.27343H18.9217L20 0ZM5.85366 5.85366L8.93453 7.41977L11.3607 4.98075L10.8216 8.38254L13.8896 9.94865L10.4878 10.4878L9.94865 13.8896L8.38254 10.8087L4.98075 11.3607L7.41977 8.9217L5.85366 5.85366ZM0 20L3.27343 18.9345V15.5071L5.28883 18.2927L8.5751 17.2272L6.54686 20.0128L8.5751 22.7985L5.28883 21.733L3.27343 24.5186V21.0783L0 20ZM5.85366 34.1463L7.41977 31.0655L4.98075 28.6393L8.38254 29.1784L9.94865 26.1104L10.4878 29.5122L13.8896 30.0513L10.8087 31.6175L11.3607 35.0193L8.9217 32.5802L5.85366 34.1463ZM20 40L18.9345 36.7266H15.5071L18.2927 34.7112L17.2272 31.4249L20.0128 33.4531L22.7985 31.4249L21.733 34.7112L24.5186 36.7266H21.0783L20 40ZM34.1463 34.1463L31.0655 32.5802L28.6393 35.0193L29.1784 31.6175L26.1104 30.0513L29.5122 29.5122L30.0513 26.1104L31.6175 29.1913L35.0193 28.6393L32.5802 31.0783L34.1463 34.1463ZM40 20L36.7266 21.0655V24.4929L34.7112 21.7073L31.4249 22.7728L33.4531 19.9872L31.4249 17.2015L34.7112 18.267L36.7266 15.4814V18.9217L40 20ZM34.1463 5.85366L32.5802 8.93453L35.0193 11.3607L31.6175 10.8216L30.0513 13.8896L29.5122 10.4878L26.1104 9.94865L29.1913 8.38254L28.6393 4.98075L31.0783 7.41977L34.1463 5.85366Z"
        fill="white"
      />
    </svg>
  )
}
